import { Modal, ModalOverlay, ModalContent, Flex, Image, Text, Box, useToast } from '@chakra-ui/react'
import { Button, Column, FormItem, TextInput, Dropdown } from '@ubirider/pick-component-library'
import React, { useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import closeButton from '../../../../assets/cross.svg'
import arrowBackIcon from '../../../../assets/arrow_left.svg'
import checkWhiteIcon from '../../../../assets/check_circle_white.svg'
import closeWhiteIcon from '../../../../assets/cross_white_24.svg'
import { addVehicle } from '../../../../api/vehicles'
import { VehicleCategories, VehicleCategoryLabels } from '../../../../consts/vehicles'
import { useVehicles } from '../../../../context/DataProvider'

export function NewVehicleModal({ isOpen, onOpen, onClose }) {
  const { t } = useTranslation()
  const windowSize = useRef([window.innerWidth, window.innerHeight])

  const { refetch } = useVehicles()

  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      model: null,
      licensePlate: null,
      category: null,
    },
  })

  const [loading, setLoading] = useState(false)

  const licensePlate = watch('licensePlate')
  const model = watch('model')
  const category = watch('category')

  useEffect(() => {
    reset({
      model: null,
      licensePlate: null,
      category: null,
    })
    setLoading(null)
  }, [isOpen, onClose, reset])

  const onSubmit = async data => {
    setLoading(true)
    try {
      addVehicle(data).then(() => {
        setLoading(false)
        handleToast()
        refetch()
        onClose()
      })
    } catch (error) {
      setLoading(false)
      console.error(error)
    }
  }

  const handleToast = () => {
    toastIdRef.current = toast({
      position: 'top',
      duration: 5000,
      isClosable: true,
      render: () => (
        <Box
          alignItems='center'
          color='white'
          bg='#01AE70'
          border='1px solid #067F5B'
          boxShadow='box-shadow: 0px 1px 10px 1px rgba(0, 0, 0, 0.05)'
          borderRadius='4px'
        >
          <Flex justifyContent='space-between' gap='16px' padding='16px'>
            <Image src={checkWhiteIcon} />
            <Text fontSize='14px' fontWeight='600'>
              {t('acqua_vias.vehicle_added_confirmation_toast')}
            </Text>
            <Image cursor='pointer' onClick={close} src={closeWhiteIcon} />
          </Flex>
        </Box>
      ),
    })
  }

  const toast = useToast()
  const toastIdRef = useRef()

  function close() {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      trapFocus={false}
      closeOnOverlayClick={false}
      autoFocus={false}
      size={{ sm: 'full', md: 'full', lg: 'xl', xl: 'xl' }}
      isCentered={windowSize.current[0] >= 960}
    >
      <ModalOverlay />
      <ModalContent padding={{ sm: '16px 24px', md: '16px 24px', lg: '36px', xl: '36px' }}>
        <Flex direction='column' gap='24px' mt={{ sm: '67px', md: '67px', lg: '0px', xl: '0px' }}>
          {windowSize.current[0] < 960 && (
            <Flex alignItems='flex-start'>
              <Button
                onClick={onClose}
                style='ghost'
                type='neutralLight'
                label={t('authentication.back')}
                leftIconProps={{ src: arrowBackIcon }}
                css={{ paddingLeft: '0' }}
              />
            </Flex>
          )}
          <Flex direction='row' justifyContent='space-between' alignItems='center'>
            <Text fontWeight='700' fontSize='24px' flex='1'>
              {t('acqua_vias.new_vehicle_dialog_title')}
            </Text>
          </Flex>

          <Column as='form' onSubmit={handleSubmit(onSubmit)} css={{ gap: '24px' }}>
            <Controller
              name='licensePlate'
              control={control}
              render={({ field }) => (
                <FormItem required label={t('acqua_vias.new_vehicle_dialog_license_plate')} error={errors.licensePlate}>
                  <TextInput placeholder={'ABC1234'} {...field} />
                </FormItem>
              )}
            />

            <Controller
              name='model'
              control={control}
              render={({ field }) => (
                <FormItem required label={t('acqua_vias.new_vehicle_dialog_brand_model')} error={errors.model}>
                  <TextInput placeholder={'Volkswagen Gol'} {...field} />
                </FormItem>
              )}
            />

            <Controller
              name='category'
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem label={t('acqua_vias.new_vehicle_dialog_category')} error={errors.category}>
                  <Dropdown
                    hasSearch
                    cells={Object.values(VehicleCategories).map(category => ({
                      label: t(VehicleCategoryLabels[category]),
                      onClick: () => onChange(category),
                      selected: category === value,
                    }))}
                    labelComponent={
                      value != null ? (
                        t(VehicleCategoryLabels[value])
                      ) : (
                        <Text fontSize='16px' color='#98A2B3'>
                          {t('acqua_vias.new_vehicle_dialog_choose_category')}
                        </Text>
                      )
                    }
                    dropdownWidth={388}
                    dropdownWidthBehaviour='expand'
                    placement='bottom-start'
                  />
                </FormItem>
              )}
            />

            <Flex
              w='100%'
              gap='16px'
              justifyContent='flex-end'
              display={{ sm: 'none', md: 'none', lg: 'flex', xl: 'flex' }}
            >
              <Button
                onClick={onClose}
                style='outline'
                type='neutralLight'
                label={t('authentication.back')}
                css={{ width: 152 }}
              />
              <Button
                disabled={!(licensePlate && model && category)}
                submit
                loading={loading}
                style='solid'
                type='secondary'
                label={t('acqua_vias.common_add')}
                css={{ width: 152 }}
              />
            </Flex>
            <Flex
              gap='16px'
              alignSelf='stretch'
              direction='column'
              display={{ sm: 'flex', md: 'flex', lg: 'none', xl: 'none' }}
            >
              <Button
                disabled={!(licensePlate && model && category)}
                submit
                loading={loading}
                style='solid'
                type='secondary'
                label={t('acqua_vias.common_add')}
                css={{ width: '100%' }}
              />
              <Button
                onClick={onClose}
                style='outline'
                type='neutralLight'
                label={t('authentication.back')}
                css={{ width: '100%' }}
              />
            </Flex>
          </Column>
        </Flex>
      </ModalContent>
    </Modal>
  )
}
